/**
 * Subscription Settings Tab
 * Manages active paywall configuration: show_on_launch, quota, and feature tiers.
 * Note: trial settings (Apple-controlled) and unused paywall fields are hidden.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Info, Save, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { toast } from 'sonner';
import { useI18n } from '@/i18n';
import { baseApiClient } from '@/lib/api/client';

// Types
interface SubscriptionConfig {
  trial: { enabled: boolean; days: number };
  paywall: { show_on_launch: boolean; delay_seconds: number; dismissable: boolean; soft_wall: boolean };
  features: { free: string[]; pro: string[]; business: string[] };
  quota: { free_messages_total: number; free_messages_daily: number };
  products: Record<string, { id: string; name: string }>;
}

// API functions
async function fetchSubscriptionConfig(): Promise<SubscriptionConfig> {
  const { data } = await baseApiClient.get<SubscriptionConfig>('/v1/subscription/config');
  return data;
}

async function updateSubscriptionConfig(payload: Record<string, unknown>): Promise<SubscriptionConfig> {
  const { data } = await baseApiClient.put<SubscriptionConfig>('/v1/subscription/admin/config', payload);
  return data;
}

// Form state — only fields that actually take effect
interface FormState {
  paywall_show_on_launch: boolean;
  quota_free_messages_total: number;
  quota_free_messages_daily: number;
  features_free: string;
  features_pro: string;
}

export function SubscriptionSettingsTab() {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['subscription-config'],
    queryFn: fetchSubscriptionConfig,
  });

  const [formState, setFormState] = useState<FormState | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form state when data loads
  if (data && !formState) {
    setFormState({
      paywall_show_on_launch: data.paywall.show_on_launch,
      quota_free_messages_total: data.quota?.free_messages_total ?? 50,
      quota_free_messages_daily: data.quota?.free_messages_daily ?? 10,
      features_free: data.features.free.join(', '),
      features_pro: data.features.pro.join(', '),
    });
  }

  const mutation = useMutation({
    mutationFn: updateSubscriptionConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-config'] });
      toast.success(t('subscriptionPage.settings.savedSuccess'));
      setHasChanges(false);
    },
    onError: (err: Error) => {
      toast.error(`${t('subscriptionPage.settings.saveFailed')}: ${err.message}`);
    },
  });

  const handleChange = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    if (!formState) return;
    setFormState({ ...formState, [key]: value });
    setHasChanges(true);
  };

  const handleSave = () => {
    if (!formState) return;

    mutation.mutate({
      paywall_show_on_launch: formState.paywall_show_on_launch,
      quota_free_messages_total: formState.quota_free_messages_total,
      quota_free_messages_daily: formState.quota_free_messages_daily,
      features_free: formState.features_free.split(',').map(s => s.trim()).filter(Boolean),
      features_pro: formState.features_pro.split(',').map(s => s.trim()).filter(Boolean),
    });
  };

  const handleReset = () => {
    if (!data) return;
    setFormState({
      paywall_show_on_launch: data.paywall.show_on_launch,
      quota_free_messages_total: data.quota?.free_messages_total ?? 50,
      quota_free_messages_daily: data.quota?.free_messages_daily ?? 10,
      features_free: data.features.free.join(', '),
      features_pro: data.features.pro.join(', '),
    });
    setHasChanges(false);
  };

  return (
    <div className="max-w-4xl">
      {/* Action Buttons */}
      <div className="flex justify-end gap-2 mb-6">
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          {t('subscriptionPage.settings.refresh')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!hasChanges || mutation.isPending}
        >
          <Save className="w-4 h-4 mr-2" />
          {mutation.isPending ? t('subscriptionPage.settings.saving') : t('subscriptionPage.settings.saveChanges')}
        </Button>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div>
              <h3 className="text-red-800 font-semibold">{t('subscriptionPage.settings.errorLoading')}</h3>
              <p className="text-red-600 text-sm">{(error as Error).message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        </div>
      )}

      {/* Form */}
      {formState && (
        <div className="space-y-6">
          {/* Paywall Settings */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('subscriptionPage.settings.paywallSettings')}</h2>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formState.paywall_show_on_launch}
                onChange={(e) => handleChange('paywall_show_on_launch', e.target.checked)}
                className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
              />
              <span className="text-gray-700">{t('subscriptionPage.settings.showOnLaunch')}</span>
            </label>
          </div>

          {/* Quota Settings */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('subscriptionPage.settings.quotaSettings')}</h2>
            <p className="text-sm text-gray-500 mb-4">{t('subscriptionPage.settings.quotaUnlimitedHint')}</p>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('subscriptionPage.settings.quotaTotalMessages')}
                </label>
                <input
                  type="number"
                  min="0"
                  value={formState.quota_free_messages_total}
                  onChange={(e) => handleChange('quota_free_messages_total', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('subscriptionPage.settings.quotaDailyMessages')}
                </label>
                <input
                  type="number"
                  min="0"
                  value={formState.quota_free_messages_daily}
                  onChange={(e) => handleChange('quota_free_messages_daily', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Feature Lists */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('subscriptionPage.settings.featuresByTier')}</h2>
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700">{t('subscriptionPage.settings.featuresByTierHint')}</p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('subscriptionPage.settings.freeFeatures')}
                </label>
                <input
                  type="text"
                  value={formState.features_free}
                  onChange={(e) => handleChange('features_free', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  placeholder="basic_chat, daily_horoscope"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('subscriptionPage.settings.proFeatures')}
                </label>
                <input
                  type="text"
                  value={formState.features_pro}
                  onChange={(e) => handleChange('features_pro', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  placeholder="unlimited_chat, birth_chart, soulmate"
                />
              </div>
            </div>
          </div>

          {/* Reset Button */}
          {hasChanges && (
            <div className="flex justify-end">
              <Button variant="outline" onClick={handleReset}>
                {t('subscriptionPage.settings.resetChanges')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
